import { Button } from '@HoudiniUI/Button';
import { CustomPageType } from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { formatDistanceToNow } from 'date-fns';
import { FC } from 'react';
import { HtmlPreviewFrame } from '../../../../components/blogs/HtmlPreviewFrame';
import { MeetingPagePreview } from '../../../../components/blogs/MeetingPagePreview';
import { InfoIcon } from '../../../../../web/components/svgs/icons/InfoIcon';
import { shadowSm } from '../../../../../web/styles/shadows';
import { THEME } from '../../../../../web/components-v2/Theme/theme';
import { PreviewContext, PreviewContextTabs } from '../PreviewContextTabs';
import { SegmentedTabs } from './SegmentedTabs';

export type Device = 'desktop' | 'mobile';

const DEVICE_OPTIONS: ReadonlyArray<{ value: Device; label: string }> = [
	{ value: 'desktop', label: 'Desktop' },
	{ value: 'mobile', label: 'Mobile' },
];

interface PreviewPaneProps {
	pageType: CustomPageType;
	previewContext: PreviewContext;
	onPreviewContextChange: (context: PreviewContext) => void;
	showContextTabs: boolean;
	device: Device;
	onDeviceChange: (device: Device) => void;
	isDirty: boolean;
	isSaving: boolean;
	lastSavedAt: Date | null;
	onSave: () => void;
	blogFeatureEnabled?: boolean;
	blogAvailable?: boolean;
	surveyFeatureEnabled?: boolean;
	surveyAvailable: boolean;
	meetingFeatureEnabled?: boolean;
	meetingAvailable: boolean;
}

export const PreviewPane: FC<PreviewPaneProps> = ({
	pageType,
	previewContext,
	onPreviewContextChange,
	showContextTabs,
	device,
	onDeviceChange,
	isDirty,
	isSaving,
	lastSavedAt,
	onSave,
	blogFeatureEnabled,
	blogAvailable,
	surveyFeatureEnabled,
	surveyAvailable,
	meetingFeatureEnabled,
	meetingAvailable,
}) => {
	const effectivePreviewContext = showContextTabs ? previewContext : 'blog';

	return (
		<div className={css(s.card)}>
			<div className={css(s.headerRow)}>
				<div className={css(s.headerLeft)}>
					<span className={css(s.title)}>Preview</span>
					{isDirty ? (
						<span className={css(s.savedCaption)}>You have unsaved changes</span>
					) : (
						lastSavedAt && (
							<span
								className={css(s.savedCaption)}
							>{`Saved ${formatDistanceToNow(lastSavedAt, { addSuffix: true })}`}</span>
						)
					)}
					<Button type='button' variant='primary' disabled={!isDirty} isLoading={isSaving} onClick={onSave}>
						Save & Update
					</Button>
				</div>
				<SegmentedTabs options={DEVICE_OPTIONS} value={device} onChange={onDeviceChange} size='sm' />
			</div>
			{showContextTabs && (
				<PreviewContextTabs
					selectedContext={previewContext}
					onContextChange={onPreviewContextChange}
					blogFeatureEnabled={blogFeatureEnabled}
					blogAvailable={blogAvailable}
					surveyFeatureEnabled={surveyFeatureEnabled}
					surveyAvailable={surveyAvailable}
					meetingFeatureEnabled={meetingFeatureEnabled}
					meetingAvailable={meetingAvailable}
				/>
			)}
			<div className={css(s.chromeBar)}>
				<span className={css(s.dot)} />
				<span className={css(s.dot)} />
				<span className={css(s.dot)} />
				<span className={css(s.urlPill)}>yourdomain.com</span>
			</div>
			<div className={css(s.frameWrapper, device === 'mobile' && s.frameWrapperMobile)}>
				{effectivePreviewContext === 'meeting' ? (
					<MeetingPagePreview />
				) : (
					<HtmlPreviewFrame type={pageType} previewContext={effectivePreviewContext} />
				)}
			</div>
			<div className={css(s.footnote)}>
				<InfoIcon fillColor={THEME.colors.icon.gray} />
				<span>Save & Update to refresh the preview.</span>
			</div>
		</div>
	);
};

const s = StyleSheet.create({
	card: {
		position: 'sticky',
		top: THEME.spacing.lg,
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		boxShadow: shadowSm,
		padding: 20, // 20px preview card padding per 1c design
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
	},
	headerRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: THEME.spacing.md,
		flexWrap: 'wrap',
	},
	headerLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.md,
	},
	savedCaption: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	title: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontWeight: THEME.typography.fontWeights.bold,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
	},
	chromeBar: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		height: 32,
		padding: `0 ${THEME.spacing.sm}px`,
		backgroundColor: THEME.colors.background.dark,
		borderRadius: THEME.common.borderRadius,
	},
	dot: {
		width: 6,
		height: 6,
		borderRadius: '50%',
		backgroundColor: THEME.colors.text.light,
	},
	urlPill: {
		flex: 1,
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
		backgroundColor: THEME.colors.background.light,
		borderRadius: THEME.common.borderRadius,
		padding: `${THEME.spacing.xs}px ${THEME.spacing.sm}px`,
		...THEME.common.truncate,
	},
	frameWrapper: {
		width: '100%',
		maxWidth: '100%',
		margin: '0 auto',
	},
	frameWrapperMobile: {
		maxWidth: 375,
	},
	footnote: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
});
